import { useParams } from "react-router-dom";
import { EditInvoiceComponent } from "./EditInvoiceComponent";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { fetchInvoicesByNumbers, selectAll } from "../invoiceDetailedSlice";
import { InvoiceEdit } from "../invoiceAPI";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import isEmpty from "../../../common/helpers/isEmpty";
import { Error } from "../../../common/components/functional/error/Error";
import {
  areInvoicesCreditInvoiceBundle,
  isNormalSingleInvoice,
} from "../utils";

export const EditInvoicePage = () => {
  const dispatch = useAppDispatch();
  const { invoiceIds } = useParams();
  const invoiceIdsArray = useMemo(() => invoiceIds?.split(","), [invoiceIds]);
  const allInvoicesFromStore = useAppSelector((state) => selectAll(state));
  const relevantInvoicesFromStore = useMemo(
    () =>
      allInvoicesFromStore.filter((invoice) =>
        invoiceIdsArray?.includes(invoice.invoiceNumber!!),
      ),
    [allInvoicesFromStore, invoiceIdsArray],
  );

  const [invoices, setInvoices] = useState<InvoiceEdit[]>([]);
  const [isCreditInvoiceBundle, setIsCreditInvoiceBundle] =
    useState<boolean>(false);
  const [triedToFetchData, setTriedToFetchData] = useState(false);

  // Check if the invoices are valid for editing
  const areInvoicesValidForEdit = (invoices: InvoiceEdit[]) => {
    // TODO credit invoice check is not correct / complete
    // -> user can input a single credit invoice number from a credit invoice bundle of 2 or more
    // -> this should fail as all bundle items should edited together
    // -> currently this is accepted
    if (areInvoicesCreditInvoiceBundle(invoices)) {
      setIsCreditInvoiceBundle(true);
      return true;
    }
    if (isNormalSingleInvoice(invoices)) return true;
    return false;
  };

  // Check if we have the relevant invoices in the store, if not, try to fetch them
  useEffect(() => {
    if (
      !isEmpty(relevantInvoicesFromStore) &&
      areInvoicesValidForEdit(relevantInvoicesFromStore)
    ) {
      setInvoices(relevantInvoicesFromStore);
      return;
    }
    if (!isEmpty(invoiceIdsArray) && !triedToFetchData) {
      dispatch(fetchInvoicesByNumbers(invoiceIdsArray as string[])).then(
        (action) => {
          if (isFulfilled(action)) {
            const fetchedInvoices = action.payload.data;
            if (areInvoicesValidForEdit(fetchedInvoices)) {
              setInvoices(fetchedInvoices);
            }
          }
          setTriedToFetchData(true);
        },
      );
    }
  }, [
    invoices,
    dispatch,
    invoiceIdsArray,
    relevantInvoicesFromStore,
    triedToFetchData,
  ]);

  // If we have tried to fetch the data and we still don't have it, show an error
  if (triedToFetchData && isEmpty(invoices)) {
    return <Error />;
  }

  return isEmpty(invoices) ? (
    <LoadingSpinner />
  ) : (
    <EditInvoiceComponent
      existingInvoices={invoices}
      isCreditInvoiceBundle={isCreditInvoiceBundle}
    />
  );
};
